import { selectIsInternalAdmin } from 'accessors/index'
import { CheckIcon } from 'components/ReusableComponents/Icons/Check'
import { ErrorIcon } from 'components/ReusableComponents/Icons/Error'
import { Cell, Row } from 'components/ReusableComponents/Table'
import BookingDetailsDialog from 'features/BookingDetailsDialog'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { formatPhoneNumber } from 'utils/helpers'

import LoyaltyIcon from '../LoyaltyIcon/index'
import styles from './ArrivalRow.module.scss'
import { ReactComponent as MessageIcon } from './message.svg'
import { ReactComponent as MessageStartedIcon } from './messageStarted.svg'
import Tooltip from './Tooltip'

const ArrivalRow = React.memo(
  ({
    subscriberHotelBookingId,
    guestFirstName,
    guestLastName,
    loyaltyProgram,
    loyaltyLevel,
    cityState,
    roomNumber,
    companyName,
    handleClick,
    chatId,
    phoneNumber,
    phoneNumberInvalid,
    checkIn,
    checkOut,
    confirmationCode,
    roomBookingCode,
    rateCode,
    bookedBy,
    bookingDate,
    bookingSource,
    subscriptionStatus,
    numberOfBookings,
    agencyParentName,
    dayBeforeCheckInMessage,
    duringCheckinMessage,
    tab
  }) => {
    const fullName = useMemo(
      () => `${guestFirstName || ''} ${guestLastName || ''}`,
      [guestFirstName, guestLastName]
    )

    const [showingTooltip, setShowingTooltip] = useState(false)
    const [isBookingDetailsDialogVisible, setIsBookingDetailsDialogVisible] =
      useState(false)

    const [activeBookingId, setActiveBookingId] = useState(null)
    const isInternalAdmin = useSelector(selectIsInternalAdmin)

    const handleBookingClick = useCallback(bookingId => {
      setActiveBookingId(bookingId)
      setIsBookingDetailsDialogVisible(true)
    }, [])

    const oneDayAgo = useMemo(
      () =>
        dayBeforeCheckInMessage.sentOn === null
          ? moment(checkIn).add(-1, 'days').format('MMM DD')
          : moment(dayBeforeCheckInMessage.sentOn).format('MMM DD'),
      [checkIn, dayBeforeCheckInMessage.sentOn]
    )

    const duringCheckin = useMemo(
      () =>
        duringCheckinMessage.sentOn === null
          ? moment(checkIn).format('MMM DD')
          : moment(duringCheckinMessage.sentOn).format('MMM DD'),
      [checkIn, duringCheckinMessage.sentOn]
    )

    return (
      <>
        {isBookingDetailsDialogVisible && (
          <BookingDetailsDialog
            bookingId={activeBookingId}
            onClose={() => setIsBookingDetailsDialogVisible(false)}
          />
        )}

        <div
          className={styles.rowHover}
          onMouseEnter={() => setShowingTooltip(true)}
          onMouseLeave={() => setShowingTooltip(false)}
        >
          <Row className={styles.row}>
            <Cell>
              <div className={styles.loyalty}>
                <LoyaltyIcon
                  loyaltyProgram={loyaltyProgram}
                  loyaltyLevel={loyaltyLevel}
                />
              </div>
              {showingTooltip && (
                <Tooltip
                  confirmationCode={confirmationCode}
                  subscriberHotelBookingId={subscriberHotelBookingId}
                  roomBookingCode={roomBookingCode}
                  rateCode={rateCode}
                  bookedBy={bookedBy}
                  bookingDate={bookingDate}
                  bookingSource={bookingSource}
                  company={companyName}
                  agencyParentName={agencyParentName}
                  subscription={['Active', 'ConsentGiven'].includes(subscriptionStatus)}
                />
              )}
            </Cell>
            <Cell className={styles.fullNameCell}>
              <button
                className={styles.fullNameButton}
                onClick={() => handleBookingClick(subscriberHotelBookingId)}
              >
                {fullName}
                {numberOfBookings > 1 && (
                  <>
                    {' '}
                    <span className={styles.numberOfBookings}>({numberOfBookings})</span>
                  </>
                )}
              </button>
            </Cell>
            <Cell className={styles.noOverflow}>
              <div className={styles.location} title={cityState}>
                <span>{cityState}</span>
              </div>
            </Cell>
            <Cell>
              <div className={styles.roomNo}>
                <span>{roomNumber}</span>
              </div>
            </Cell>
            <Cell>
              <div className={styles.checkIn}>{moment(checkIn).format('MMM D')}</div>
            </Cell>
            <Cell>
              <div className={styles.checkOut}>{moment(checkOut).format('MMM D')}</div>
            </Cell>
            <Cell>
              {(phoneNumber || phoneNumberInvalid) && (
                <span className={styles.phone}>
                  {phoneNumberInvalid ? (
                    <ErrorIcon title="wrong phone number" className={styles.errorIcon} />
                  ) : (
                    <CheckIcon title="correct phone number" />
                  )}
                  {formatPhoneNumber(phoneNumber) || phoneNumberInvalid}
                </span>
              )}
            </Cell>
            <Cell>
              <div
                className={
                  dayBeforeCheckInMessage.isSent
                    ? styles.inPropertyGreen
                    : styles.inPropertyGray
                }
                data-tip={dayBeforeCheckInMessage.reason}
              >
                {dayBeforeCheckInMessage.isSent ? (
                  <CheckIcon />
                ) : (
                  isInternalAdmin && <ErrorIcon />
                )}
                {oneDayAgo}
              </div>
            </Cell>
            {tab === 'checkin' && (
              <Cell>
                <div
                  className={
                    duringCheckinMessage.isSent
                      ? styles.inPropertyGreen
                      : styles.inPropertyGray
                  }
                  data-tip={duringCheckinMessage.reason}
                >
                  {duringCheckinMessage.isSent ? (
                    <CheckIcon />
                  ) : (
                    isInternalAdmin && <ErrorIcon />
                  )}
                  {duringCheckin}
                </div>
              </Cell>
            )}
            <Cell>
              <div className={styles.cta}>
                {chatId ? (
                  <Link to={`/messaging/${chatId}`} className={styles.ctaHoverButton}>
                    <button>
                      <MessageStartedIcon />
                      View chat
                    </button>
                  </Link>
                ) : (
                  <button onClick={() => handleClick(subscriberHotelBookingId)}>
                    <MessageIcon />
                    Start chat
                  </button>
                )}
              </div>
            </Cell>
          </Row>
        </div>
      </>
    )
  }
)

ArrivalRow.propTypes = {
  subscriberHotelBookingId: PropTypes.number.isRequired,
  clientFullName: PropTypes.string,
  guestFirstName: PropTypes.string,
  guestLastName: PropTypes.string,
  cityState: PropTypes.string,
  roomNumber: PropTypes.string,
  trackingInfo: PropTypes.string,
  comments: PropTypes.string,
  handleClick: PropTypes.func.isRequired,
  loyaltyLevel: PropTypes.string,
  loyaltyProgram: PropTypes.string,
  companyName: PropTypes.string,
  chatId: PropTypes.number
}

export default ArrivalRow
